import { useTranslation } from '@spcc/core'
import { cn, Badge, Button, Skeleton } from '@spcc/uikit'
import type { WeighbridgeQueueItem, WeighStage } from '../../domain/types'
import { getFlowDirection, getFlowIdentifier, getFlowQueueKey } from '../../domain/utils/flow-identity'
import {
  formatWait,
  formatWeight,
  getDisplayedWeight,
  hasWeight,
  packagingIcon,
} from './active-queue-panel.utils'

interface ActiveQueuePanelProps {
  queue: WeighbridgeQueueItem[]
  stage?: WeighStage
  onRemove?: (item: WeighbridgeQueueItem, stage?: WeighStage) => void
  className?: string
}

export function ActiveQueuePanel({ queue, stage, onRemove, className }: ActiveQueuePanelProps) {
  const { t } = useTranslation()

  const filtered = stage ? queue.filter((q) => q.stage === stage) : queue

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t('weighbridgeOps.queue.title')}
        </h4>
        {filtered.length > 0 && (
          <Badge variant="secondary" className="text-xs">
            {filtered.length}
          </Badge>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="flex h-14 items-center justify-center rounded-lg border border-dashed border-muted-foreground/30 bg-muted/20">
          <span className="text-xs text-muted-foreground">{t('weighbridgeOps.queue.empty')}</span>
        </div>
      ) : (
        <ul className="space-y-1.5 max-h-52 overflow-y-auto">
          {filtered.map((item) => (
            <li
              key={getFlowQueueKey(item)}
              className={cn(
                'flex flex-col gap-1 rounded-lg px-3 py-2 text-xs border',
                item.status !== 'waiting'
                  ? 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20 font-medium'
                  : 'border-amber-200 bg-amber-50/60 dark:border-amber-800 dark:bg-amber-900/10',
              )}
            >
              {/* ── Row 1: order number + status + wait time + remove ── */}
              <div className="flex items-center gap-2">
                {item.status !== 'waiting' ? (
                  <span className="h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse shrink-0" />
                ) : (
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-400 shrink-0" />
                )}
                <span className="font-mono flex-1 truncate">{getFlowIdentifier(item)}</span>
                <Badge
                  variant={getFlowDirection(item) === 'inbound' ? 'default' : 'secondary'}
                  className="text-[10px] px-1.5 py-0 shrink-0"
                >
                  {getFlowDirection(item) === 'inbound'
                    ? t('weighbridgeOps.queue.inbound')
                    : t('weighbridgeOps.queue.outbound')}
                </Badge>
                {item.status === 'waiting' && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-400/60 text-amber-600 dark:text-amber-400 shrink-0">
                    {t('weighbridgeOps.queue.statusWaiting')}
                  </Badge>
                )}
                {item.enqueuedAt && (
                  <span className="text-muted-foreground shrink-0">{formatWait(item.enqueuedAt, t)}</span>
                )}
                {onRemove && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 w-5 shrink-0 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                    title={t('weighbridgeOps.queue.removeFromQueueTitle', {
                      orderNumber: getFlowIdentifier(item),
                    })}
                    onClick={() => onRemove(item, item.stage)}
                  >
                    ✕
                  </Button>
                )}
              </div>

              {/* ── Row 2: enrichment details (driver, plate, packaging, weights) ── */}
              {item.detailsFetching ? (
                <div className="flex gap-2 ps-3.5">
                  <Skeleton className="h-3 w-24 rounded" />
                  <Skeleton className="h-3 w-20 rounded" />
                </div>
              ) : (
                (
                  item.driverName ||
                  item.vehicleLicensePlate ||
                  item.variantPackaging != null ||
                  item.plannedWeight != null ||
                  hasWeight(item.firstWeight, item.firstWeightKg) ||
                  hasWeight(item.secondWeight, item.secondWeightKg)
                ) && (
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 ps-3.5 text-[11px] text-muted-foreground">
                    {item.vehicleLicensePlate && (
                      <span title={t('weighbridgeOps.queue.plate')}>
                        🚛 {item.vehicleLicensePlate}
                      </span>
                    )}
                    {item.driverName && (
                      <span title={t('weighbridgeOps.queue.driver')}>
                        👤 {item.driverName}
                      </span>
                    )}
                    {item.plannedWeight != null && (
                      <span title={t('weighbridgeOps.queue.plannedWeight')}>
                        {packagingIcon(item.variantPackaging)} {t('weighbridgeOps.queue.plannedWeightValue', { weight: item.plannedWeight.toLocaleString() })}
                      </span>
                    )}
                    {(hasWeight(item.firstWeight, item.firstWeightKg) || item.stage === 'first_weight' || item.stage === 'second_weight') && (
                      <span title={t('weighbridgeOps.queue.firstWeight')}>
                        {t('weighbridgeOps.queue.firstWeight')}: {formatWeight(getDisplayedWeight(item.firstWeight, item.firstWeightKg), t)}
                      </span>
                    )}
                    {(hasWeight(item.secondWeight, item.secondWeightKg) || item.stage === 'second_weight') && (
                      <span title={t('weighbridgeOps.queue.secondWeight')}>
                        {t('weighbridgeOps.queue.secondWeight')}: {formatWeight(getDisplayedWeight(item.secondWeight, item.secondWeightKg), t)}
                      </span>
                    )}
                  </div>
                )
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
